import { isPraise } from '@/lib/who-match';
import { useCallback, useState, useEffect, useRef, useMemo, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { Prayer, PrayerStatus } from '@/types/prayer';
import {
  useSafeTestimonyEdit,
  type TestimonyLayerControl,
} from '@/hooks/useTestimonyEdit';
import type { TestimonyDraftPurpose } from '@/types/testimony-draft';
import { topmostModal, useDialogFocus } from '@/hooks/useDialogFocus';
import { useSaveCue } from '@/hooks/useSaveCue';
import { useAdiaStore } from '@/lib/store';
import { PrayerAuthoringSession, prayerEditorAuth, prayerEditorIsCurrent, savePrayerIntent } from '@/lib/prayer-authoring';
import { materializeProgressEntryIds } from '@/lib/progress-entry-identity';
import { parsePrayer } from '@/lib/grok';
import { shouldShowNameHint } from '@/lib/name-hint';
import { buildPrivacySafeEnrichmentContext } from '@/lib/privacy-safe-enrichment';
import { prayerContentMatches, prayerContentSnapshot, prayerOutboundExpectation } from '@/lib/outbound-prayer-authorization';
import {
  whoOf,
  statusOf,
  formatFullDateTime,
  formatUpdateDate,
  isSelf,
} from '@/lib/prayer-utils';
import CopyButton from '@/components/CopyButton';
import MicButton from '@/components/MicButton';
import LetThemKnowSheet from '@/components/LetThemKnowSheet';
import MailCardSheet from '@/components/MailCardSheet/MailCardSheet';
import ChoiceSheet from '@/components/ChoiceSheet/ChoiceSheet';
import ScriptureVersesSheet from '@/components/ScriptureVersesSheet/ScriptureVersesSheet';
import { PICKER_ENABLED, VERSE_SUGGEST_ENABLED } from '@/lib/feature-flags';
import LeafButton from '@/components/LeafButton';
import LeafActionSheet, { type LeafDeleteState } from '@/components/LeafActionSheet';
import { useNavLayer, useNavStack } from '@/hooks/useNavLayer';
import { useNoticeStackClearance, useNoticeStackKeyboardRoom, useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import { isLiveSharePrayer } from '@/lib/share-live';
import { resolveSavedVerses } from '@/lib/verse-result';
import type { SuggestedVerse } from '@/lib/verse-suggest';
import { isCrisisPrayer } from '@/lib/spine-crisis';
import NameHintInline from '@/components/NameHintInline';
import TranscriptionRecoveryBanner from './TranscriptionRecoveryBanner';
import UnconfirmedSaveNotice from './UnconfirmedSaveNotice';
import { finalizeCommittedAudio, parkTypedRecoveryAudio, typedRecoveryRefusedOnlyForUnreadableRecording } from '@/lib/audio-queue';
import { useAudioQueueStore } from '@/lib/audio-queue-store';
import { ordinaryDeleteSupported } from '@/lib/keepsake/prayer-actions';
import { enhancedVoiceAllowed, isCoreReadOnly } from '@/lib/core-cutover';
import {
  type ProgressEntryIdentity,
} from '@/lib/prayer-edit-safety';
import { refreshPrayerEnrichment } from '@/lib/prayer-enrichment';
import type { ContentLifecycleAuthExpectation } from '@/lib/content-lifecycle/store';
import { syncContentLifecycle } from '@/lib/content-lifecycle/sync';
import styles from './PrayerDetail.module.css';

interface PrayerDetailProps {
  prayer: Prayer;
  onClose: () => void;
  onDelete: (id: string) => Promise<void>;
  onNew: () => void;
  onPersonTap?: (who: string) => void;
  /** Coordinator-computed z for the `detail` layer root (A1). */
  zIndex?: number;
  /**
   * One-shot request (Settings "Notes you started" Open) to open this
   * prayer's testimony dialog after mount: the leaf first, then the dialog
   * above it, exactly as the leaf's own action does. Presentation only.
   */
  initialTestimony?: string | null;
  /** The request was used or set aside; the owner clears it. */
  onInitialTestimonyDone?: (id: string) => void;
}

function EditableHistoryEntry({
  date,
  note,
  onSave,
  onRemove,
}: {
  date: string;
  note: string;
  onSave: (newNote: string) => Promise<boolean>;
  onRemove: () => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note);
  const handlersRef = useRef({ onSave, onRemove });
  const historyAuthRef = useRef(prayerEditorAuth());
  const [saveError, setSaveError] = useState(false);

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setSaveError(false);
    const saved = await handlersRef.current.onSave(trimmed);
    if (!prayerEditorIsCurrent(historyAuthRef.current)) return;
    if (saved) setEditing(false); else setSaveError(true);
  };

  const handleRemove = async () => {
    setSaveError(false);
    const removed = await handlersRef.current.onRemove();
    if (prayerEditorIsCurrent(historyAuthRef.current) && !removed) setSaveError(true);
  };

  const handleCancel = () => {
    setDraft(note);
    setEditing(false);
  };

  if (editing) {
    return (
      <li className={styles.historyEntry}>
        <span className={styles.historyDate}>{formatUpdateDate(date)}</span>
        <textarea
          className={styles.historyEditInput}
          value={draft}
          onChange={(e) => { setSaveError(false); setDraft(e.target.value); }}
          aria-label={t('detail.editUpdateInputAria')}
          rows={2}
        />
        {saveError && (
          <p className={styles.inlineError} role="alert">{t('detail.saveError')}</p>
        )}
        <div className={styles.historyEditActions}>
          <button type="button" className={styles.historyEditSave} onClick={handleSave}>
            {t('detail.save')}
          </button>
          <button type="button" className={styles.historyEditCancel} onClick={handleCancel}>
            {t('detail.cancel')}
          </button>
          <button type="button" className={styles.historyEditRemove} onClick={handleRemove}>
            {t('detail.deleteAction')}
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className={styles.historyEntry}>
      <div className={styles.historyTop}>
        <span className={styles.historyDate}>{formatUpdateDate(date)}</span>
        <div className={styles.historyActions}>
          {note.trim() && <CopyButton text={note} label={t('detail.copyUpdateAria')} />}
          {/* Cutover: editing an update is a prayer write. Copy stays. */}
          {!isCoreReadOnly() && (
          <button
            type="button"
            className={styles.historyEditBtn}
            onClick={() => { handlersRef.current = { onSave, onRemove }; setDraft(note); setEditing(true); }}
            aria-label={t('detail.editUpdateAria')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          )}
        </div>
      </div>
      <p className={styles.historyNote}>{note}</p>
    </li>
  );
}

const STATUS_VISUAL: Record<PrayerStatus, { dot: string; label: string }> = {
  Open: { dot: styles.dotOpen, label: styles.statusLabelOpen },
  'In Progress': { dot: styles.dotInProgress, label: styles.statusLabelInProgress },
  Answered: { dot: styles.dotAnswered, label: styles.statusLabelAnswered },
  Moving: { dot: styles.dotMoving, label: styles.statusLabelMoving },
};

export default function PrayerDetail({
  prayer,
  onClose,
  onDelete,
  onNew,
  onPersonTap,
  zIndex,
  initialTestimony = null,
  onInitialTestimonyDone,
}: PrayerDetailProps) {
  const { t, i18n } = useTranslation();
  const nav = useNavStack();
  const location = useLocation();
  // LC4: why the queue run cannot process this prayer's pending recording in
  // this page (page memory only). Shown instead of a finishing indicator.
  const recordingAttention = useAudioQueueStore((s) =>
    prayer.pendingTranscriptionId ? s.attention[prayer.pendingTranscriptionId] : undefined);
  // LC7: `missing` (no recording row on this device) applies whatever the
  // voice gate or the failure flag says: neither "safely stored" nor Retry or
  // Remove would be true or work for a recording that is not here.
  const pendingAttention =
    prayer.pendingTranscriptionId &&
    (recordingAttention === 'missing' || (!prayer.transcriptionFailed && enhancedVoiceAllowed()))
      ? recordingAttention
      : undefined;
  // The recovery banner's render gate (below): a terminal failure, a page
  // attention mark, or a pending recording kept while Enhanced Voice is off.
  const recoveryBannerShown =
    Boolean(prayer.transcriptionFailed || pendingAttention ||
      (prayer.pendingTranscriptionId && !enhancedVoiceAllowed())) &&
    !isCoreReadOnly();
  // RU item 1 (2026-10-20): the ordinary Delete cannot work for a pending
  // prayer that has a recording and no content-lifecycle entry
  // (ordinaryDeleteSupported reads the durable entry; deletePrayerProtected
  // refuses exactly that state), so the leaf offers none and names what this
  // page offers instead, from the same inputs as the banner's actions. The
  // read is renewed whenever the prayer changes, so Delete returns when the
  // words land (the landing creates the entry) or are typed.
  const deleteCheckKey = prayer.pendingTranscriptionId
    ? `${prayer.id}|${prayer.pendingTranscriptionId}|${prayer.updatedAt}`
    : null;
  const [deleteCheck, setDeleteCheck] = useState<{ key: string; supported: boolean } | null>(null);
  useEffect(() => {
    if (!deleteCheckKey) return;
    let live = true;
    ordinaryDeleteSupported(prayer)
      .then((supported) => { if (live) setDeleteCheck({ key: deleteCheckKey, supported }); })
      // A failed read keeps today's Delete, which reports its own failure.
      .catch(() => { if (live) setDeleteCheck({ key: deleteCheckKey, supported: true }); });
    return () => { live = false; };
  }, [deleteCheckKey, prayer]);
  const leafDeleteState: LeafDeleteState = !deleteCheckKey
    ? 'available'
    : deleteCheck?.key !== deleteCheckKey
      ? 'checking'
      : deleteCheck.supported
        ? 'available'
        // The banner offers Remove with Enhanced Voice on, for a failed
        // prayer or a recording that is not on this device.
        : recoveryBannerShown && enhancedVoiceAllowed() &&
            (pendingAttention === undefined || pendingAttention === 'missing')
          ? 'remove'
          // Otherwise it offers "Type what you remember" unless the page
          // cannot read the recording (no action until the app is reopened;
          // fix round 1, F4: the note says to reopen so it can finish).
          : recoveryBannerShown && pendingAttention === 'unreadable'
            ? 'reopen'
            : recoveryBannerShown
              ? 'type'
              : 'wait';
  // A2 edit-mode commit-on-close gating: true => the layer close() commits the
  // draft (the Back gesture, operator decision 7). Save/Cancel/Enter/Escape flip
  // these false so the close() they trigger via the [isOpen] teardown does not
  // double-commit (A2-plan §3.1).
  const rawCommitsOnCloseRef = useRef(true);
  const whoCommitsOnCloseRef = useRef(true);
  // Armed by handleTestimonyCommitted (capture path) only on a success that
  // closes the dialog; closeTestimony fires the celebration when the testimony
  // layer actually pops (modal gone), not under it, and clears it.
  const pendingCelebrationRef = useRef(false);
  // The open testimony dialog's handle for its layer close (plan rev 4, 6.3).
  const testimonyControlRef = useRef<TestimonyLayerControl | null>(null);
  // After an in-app dismissal or a completed save, focus returns to the leaf
  // button (the leaf card that opened the dialog closes with it).
  const focusLeafAfterTestimonyRef = useRef(false);
  const leafButtonRef = useRef<HTMLButtonElement | null>(null);
  // App notice stack: the first in-flow child (the Back and New buttons are
  // absolute) keeps `.detailContent` below the fixed notices. Chrome only; `.detail` keeps scrolling as before.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();
  // When true, the LetThemKnowSheet overlay is mounted. Sheet is the
  // user's preview + Copy + Download surface — opened via the leaf
  // sheet's Send-a-card card.
  const [shareOpen, setShareOpen] = useState(false);
  // Flag-gated MailCardSheet overlay (Phase 2A — replaces Picker mount
  // per docs/plans/send-a-card-phase-2a-foundation-2026-05-27.md § 5.1).
  // When PICKER_ENABLED, both Send-a-card entry points route to this
  // overlay instead of LTKS. The two state slots live side-by-side; the
  // active one is chosen at handler time by the flag. LTKS mount JSX
  // stays in source for the flag-off path. PICKER_ENABLED flag name
  // is retained for 2A (banked rename to MAILCARD_ENABLED at Phase 5
  // alongside Picker.tsx deletion).
  const [mailCardOpen, setMailCardOpen] = useState(false);
  // Flag-gated choice sheet between Send-a-card and destination
  // (M.3.7.b.1.a). Only set when PICKER_ENABLED is true; when the
  // flag is off the choice sheet never mounts and Send-a-card opens
  // LTKS directly. Independent from shareOpen and mailCardOpen so the
  // three surfaces never collide.
  const [sendChoiceOpen, setSendChoiceOpen] = useState(false);
  const detailAuthRef = useRef(prayerEditorAuth());
  const rawBaseRef = useRef(prayer);
  const rawSessionRef = useRef(new PrayerAuthoringSession(prayerEditorAuth()));
  const whoBaseRef = useRef(prayer);
  const whoSessionRef = useRef(new PrayerAuthoringSession(prayerEditorAuth()));
  const [editingRaw, setEditingRaw] = useState(false);
  const [rawDraft, setRawDraft] = useState(prayer.rawText);
  const [rawSaveError, setRawSaveError] = useState(false);
  const [rawSavePending, setRawSavePending] = useState(false);
  const [retryingParse, setRetryingParse] = useState(false);
  const [editingWho, setEditingWho] = useState(false);
  const [whoDraft, setWhoDraft] = useState(whoOf(prayer));
  const [whoSaveError, setWhoSaveError] = useState(false);
  const [whoSavePending, setWhoSavePending] = useState(false);
  const [testimonyMode, setTestimonyMode] = useState<'none' | 'capture' | 'edit'>('none');
  const [testimonyKeyboardInset, setTestimonyKeyboardInset] = useState(0);
  // Brief dignified moment played when a prayer transitions to Answered.
  // Soft gold halo + single botanical bloom. NOT confetti. ~1.4s total.
  const [celebrating, setCelebrating] = useState(false);
  // Sacred Leaf state. The discovery-glow first-visit machinery was
  // dropped in Sub-chunk D — LeafButton now plays the persistent
  // breathing halo unconditionally so a separate seen-tracking pass
  // adds no behavior. settings-store's leafGlowSeenIds storage is
  // intentionally left intact to avoid dirtying users' localStorage.
  const [leafSheetOpen, setLeafSheetOpen] = useState(false);

  // iOS keyboard-safety for the testimony modal (core rule #12).
  useEffect(() => {
    if (testimonyMode === 'none') {
      setTestimonyKeyboardInset(0);
      return;
    }
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const diff = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setTestimonyKeyboardInset(diff);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [testimonyMode]);

  const detailStatus = statusOf(prayer);
  const detailParsed = prayer.parsed;
  const detailHistory = materializeProgressEntryIds(prayer).progressHistory;
  const showMovingSuggestion =
    detailParsed?.suggestedStatus === 'GodIsMoving' &&
    detailStatus === 'Open';

  const prayers = useAdiaStore((s) => s.prayers);

  // Single source of truth for the inline NameHint trigger. Both the
  // NameHintInline render gate and the lit-up Edit button affordance
  // read this same boolean — computing once here avoids drift between
  // the two surfaces and keeps the en-locale gate centralized inside
  // shouldShowNameHint.
  const showNameHint = shouldShowNameHint(prayer, prayers, i18n.language);

  const reparseInBackground = (
    rawText: string,
    opts?: { takeFreshMentioned?: boolean },
  ): Promise<void> => {
    // Read fresh prayers from the store (not the closure-captured prop) so
    // existingPeopleList reflects the user's just-saved edit. Closure-stale
    // hints would feed Grok the pre-edit corpus, which is what reverted user
    // edits before the fix.
    const auth = prayerEditorAuth();
    const freshPrayers = useAdiaStore.getState().prayers;
    const targetPrayer = freshPrayers.find((candidate) => candidate.id === prayer.id);
    if (!targetPrayer || targetPrayer.rawText !== rawText) return Promise.resolve();
    const targetSnapshot = prayerContentSnapshot(targetPrayer);
    const { relatedContext, existingPeople, contextExpectations } = buildPrivacySafeEnrichmentContext(
      rawText,
      freshPrayers,
      prayer.id,
    );
    return parsePrayer(rawText, relatedContext || undefined, existingPeople, {
      target: prayerOutboundExpectation(targetPrayer),
      context: contextExpectations,
    }).then(async (parsed) => {
      await useAdiaStore.getState().applyEnrichment(prayer.id,
        current => prayerEditorIsCurrent(auth) && prayerContentMatches(targetSnapshot, current) && current.rawText === rawText,
        current => ({ ...current, parsed: current.userEditedFields?.who && current.parsed
          ? { ...parsed, who: current.parsed.who, mentionedPeople: opts?.takeFreshMentioned ? parsed.mentionedPeople : current.parsed.mentionedPeople }
          : parsed }));
    }).catch(() => {});
  };

  const parseNeedsAttention =
    prayer.rawText.trim() !== '' &&
    !prayer.pendingTranscriptionId &&
    (
      prayer.parsed === null ||
      (prayer.userEditedFields?.who === true && prayer.parsed.core_prayer === '')
    );

  const handleRetryParse = async () => {
    setRetryingParse(true);
    try {
      await reparseInBackground(prayer.rawText);
    } finally {
      setRetryingParse(false);
    }
  };

  const handleBack = () => {
    onClose();
  };

  const handleDelete = async () => {
    await onDelete(prayer.id);
  };

  // Bubbled up from LeafActionSheet's Send-a-card row. The share flow opens
  // only while the prayer still exists in the live store.
  const handleSendCardFromLeaf = () => {
    if (!isLiveSharePrayer(prayer.id)) return;
    if (PICKER_ENABLED) {
      setSendChoiceOpen(true);
    } else {
      setShareOpen(true);
    }
  };

  // Read-only "Scripture for this prayer" view (rule #80, the verse lane;
  // cut-tap-to-match plan sections 4/5). PURE READ: the resolver reads
  // prayer.verseResult and resolves it from the local bundle — no
  // matching API, no paid endpoint, no rawText to Grok, no Supabase
  // write. Searching for scripture happens in the share flow + the Garden
  // card's "Find" action (Chunk 2); this detail page just READS what a
  // search already saved. Crisis is handled
  // inside resolveSavedVerses (it returns []). Keyed off
  // the prayer object so a raw-text edit (which clears verseResult)
  // re-resolves to [] and hides the line, and a fresh share-flow find
  // re-resolves to the new set.
  const [scriptureVersesOpen, setScriptureVersesOpen] = useState(false);
  const verseLaneEligible =
    VERSE_SUGGEST_ENABLED && (i18n.language ?? 'en').slice(0, 2) === 'en';

  // Stable VALUE identity for the CURRENT prayer's saved verse result.
  // Encodes everything resolveSavedVerses() reads: blank rawText and crisis
  // both fold in as null (synchronous suppression — a crisis-matching or
  // emptied rawText hides the line immediately, never waiting on the async
  // resolve), and the saved result is keyed by matchedAt + every entry
  // ref/category. Because it's a string (not an object), a harmless
  // store-swap that leaves these fields unchanged keeps the key EQUAL — no
  // flash, no re-resolve, no spurious sheet close. null = nothing to show.
  const verseKey = useMemo(() => {
    if (!verseLaneEligible) return null;
    const vr = prayer.verseResult;
    if (!vr || vr.entries.length === 0) return null;
    if (!prayer.rawText.trim()) return null;
    if (isCrisisPrayer(prayer.rawText)) return null;
    const entriesKey = vr.entries.map((e) => `${e.ref}|${e.category}`).join(',');
    return `${prayer.id}::${vr.matchedAt}::${entriesKey}`;
  }, [verseLaneEligible, prayer.id, prayer.rawText, prayer.verseResult]);

  // Resolved verses TAGGED with the key they were resolved for. The render
  // gate (resolved.key === verseKey) means a result from a prior prayer or a
  // prior saved-result can never display against the current key — closes
  // both the cross-prayer and post-edit stale windows without a synchronous
  // clear. Stale `resolved` left behind on a key change is inert (the gate
  // rejects it).
  const [resolved, setResolved] = useState<{
    key: string;
    verses: SuggestedVerse[];
  } | null>(null);
  useEffect(() => {
    if (verseKey === null) return;
    let cancelled = false;
    void resolveSavedVerses(prayer).then((vs) => {
      if (!cancelled) setResolved({ key: verseKey, verses: vs });
    });
    return () => {
      cancelled = true;
    };
    // verseKey encodes every input resolveSavedVerses reads (id + matchedAt
    // + entry refs/categories; blank/crisis -> verseKey === null). Depending
    // on the prayer OBJECT would re-resolve on harmless store-swap churn.
    // rule #75.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verseKey]);

  // Current-key resolved set ([] until the matching resolve lands, or on
  // bundle drift where resolveSavedVerses returns []).
  const scriptureVerses =
    resolved !== null && resolved.key === verseKey ? resolved.verses : [];
  const showScriptureLine = verseKey !== null && scriptureVerses.length > 0;

  // Close the open saved-verse sheet when its content goes empty/stale
  // (raw-text edit nulls verseResult, crisis, drift) — close the NavStack
  // layer, never just stop rendering (history desync, rule #78). Driven by
  // the keyed showScriptureLine so harmless object churn never closes it.
  useEffect(() => {
    if (scriptureVersesOpen && !showScriptureLine) {
      nav.requestClose('scripture-verses');
    }
  }, [scriptureVersesOpen, showScriptureLine, nav]);

  const handleEditWho = async (newWho: string) => {
    const original = whoBaseRef.current;
    const trimmed = newWho.trim();
    if (!trimmed) return;
    const result = await whoSessionRef.current.save(prayer.id, { kind: 'who', expected: original.parsed?.who ?? null,
      expectedManual: Boolean(original.userEditedFields?.who), who: trimmed });
    if (result.kind !== 'committed') throw new Error('prayer-attempt-not-pending');
    if (!prayerEditorIsCurrent(whoSessionRef.current.auth)) return;
    void reparseInBackground(result.prayer.rawText, { takeFreshMentioned: !original.parsed });
  };

  const handleKeepGeneral = async () => {
    await savePrayerIntent(prayer.id, { kind: 'who', expected: prayer.parsed?.who ?? null,
      expectedManual: Boolean(prayer.userEditedFields?.who), who: 'General' }, detailAuthRef.current);
    void reparseInBackground(prayer.rawText);
  };

  const handleMarkMoving = async () => {
    const entry = materializeProgressEntryIds(prayer).progressHistory[0];
    await savePrayerIntent(prayer.id, { kind: 'status', expected: prayer.status,
      expectedManual: Boolean(prayer.userEditedFields?.status), status: 'Moving',
      ...(entry?.id ? { movingEntry: { id: entry.id, date: entry.date, note: entry.note } } : {}) }, detailAuthRef.current);
  };

  const handleEditRawText = async (newText: string) => {
    const original = rawBaseRef.current;
    const queueItemId = original.pendingTranscriptionId;
    const voiceEnabled = enhancedVoiceAllowed();
    // LC4: a recording whose bytes do not match their reference is never sent;
    // typing keeps it (made terminal, byte-preserved, never consumed), as the
    // voice-off path does, instead of claiming it for this commit.
    // LC7: a recording that is not on this device can only be kept too: the
    // typed words go through the missing-row rescue, which rereads the durable
    // Prayer and requires it to still name this exact queue id.
    const attentionAtSave = queueItemId ? useAudioQueueStore.getState().attention[queueItemId] : undefined;
    let keepRecording = Boolean(queueItemId) && (
      attentionAtSave === 'missing' ||
      (voiceEnabled && !original.transcriptionFailed && attentionAtSave === 'mismatch')
    );
    const commitTypedEdit = async (_durableBase?: Prayer, expectedAuth?: ContentLifecycleAuthExpectation): Promise<Prayer> => {
      if (expectedAuth && (rawSessionRef.current.auth.ownerId !== expectedAuth.ownerId || rawSessionRef.current.auth.authEpoch !== expectedAuth.authEpoch)) throw new Error('content-lifecycle-context-stale');
      const result = await rawSessionRef.current.save(prayer.id,
        { kind: 'raw', expected: original.rawText, text: newText, ...(queueItemId ? { pendingQueueId: queueItemId } : {}) },
        queueItemId && voiceEnabled && !keepRecording ? [{ queueItemId, recoveryKind: 'typed-replacement' }] : []);
      if (result.kind !== 'committed') throw new Error('prayer-attempt-not-pending');
      return result.prayer;
    };
    let updated: Prayer;
    if (queueItemId && (!voiceEnabled || keepRecording)) {
      updated = await parkTypedRecoveryAudio(queueItemId, original.id, commitTypedEdit, { keepRecording });
    } else {
      try {
        updated = await commitTypedEdit();
      } catch (error) {
        // D3 (LC5; the person's words are saved reliably): the claim was refused
        // only because this recording's own bytes cannot be read in this page.
        // The words are committed without consuming it, as the voice-off path
        // does: the recording stays kept and terminal and never lands here.
        if (!queueItemId || !(await typedRecoveryRefusedOnlyForUnreadableRecording(error, queueItemId))) throw error;
        keepRecording = true;
        updated = await parkTypedRecoveryAudio(queueItemId, original.id, commitTypedEdit, { keepRecording: true });
      }
    }
    if (queueItemId && voiceEnabled && !keepRecording) void syncContentLifecycle().then(() => finalizeCommittedAudio(queueItemId)).catch(() => {});
    if (updated.pendingEnrichment) void refreshPrayerEnrichment(updated.id).catch(() => {});
  };

  const handleEditHistoryEntry = async (identity: ProgressEntryIdentity, newNote: string): Promise<boolean> => {
    if (!identity.id) return false;
    try {
      await savePrayerIntent(prayer.id, { kind: 'entry-edit', entry: { ...identity, id: identity.id }, text: newNote }, detailAuthRef.current);
      void refreshPrayerEnrichment(prayer.id).catch(() => {});
      return true;
    } catch { return false; }
  };

  const handleRemoveHistoryEntry = async (identity: ProgressEntryIdentity): Promise<boolean> => {
    if (!identity.id) return false;
    try {
      await savePrayerIntent(prayer.id, { kind: 'entry-remove', entry: { ...identity, id: identity.id } }, detailAuthRef.current);
      void refreshPrayerEnrichment(prayer.id).catch(() => {});
      return true;
    } catch { return false; }
  };

  const handleToggleAnswered = async () => {
    if (detailStatus === 'Answered') {
      // Reopening — simple toggle back to Open.
      await savePrayerIntent(prayer.id, { kind: 'status', expected: prayer.status,
        expectedManual: Boolean(prayer.userEditedFields?.status), status: 'Open' }, detailAuthRef.current);
    } else {
      // Opening the testimony capture prompt. Only the dialog's explicit Save
      // or "Mark answered without a note" writes the prayer; no dismissal does.
      setTestimonyMode('capture');
    }
  };

  const openEditTestimony = () => {
    setTestimonyMode('edit');
  };

  // Plays a brief gold-halo + botanical-bloom moment when a prayer crosses
  // into Answered. Respects prefers-reduced-motion (shorter, no scale).
  const celebrateAnswered = () => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    setCelebrating(true);
    setTimeout(() => setCelebrating(false), reduced ? 600 : 1400);
  };

  // The `testimony` layer close() (A2). Fires on the reconciler pop — both the
  // Back gesture AND exitTestimonyToDetail's completion pop route through here,
  // so it is the SINGLE testimony close path. Safe dismissal (plan rev 4,
  // sections 6.3 and 8.4): while the dialog's text is not yet durable, a write
  // or a recording hold is in flight, or its capture is listening, one step of
  // Back re-registers the layer instead of unmounting, and the dialog finishes
  // its own exit. Only a multi-entry jump ("forced": the layer below is gone
  // too) may drop a GENUINELY LISTENING capture, and the dialog decides that.
  // Once the dialog has unmounted, nothing here cancels anything. The
  // celebration is started HERE (when the modal actually pops) — requestClose
  // is async, so starting it earlier would run it under the still-visible modal.
  const closeTestimony = () => {
    const control = testimonyControlRef.current;
    if (control) {
      const kind = nav.has('leaf') ? 'one-step' : 'forced';
      if (control.onLayerClose(kind) === 'keep') {
        if (!nav.has('testimony')) nav.push('testimony', closeTestimony);
        return;
      }
    }
    setTestimonyMode('none');
    if (pendingCelebrationRef.current) {
      pendingCelebrationRef.current = false;
      celebrateAnswered();
    }
    if (focusLeafAfterTestimonyRef.current) {
      focusLeafAfterTestimonyRef.current = false;
      requestAnimationFrame(() => leafButtonRef.current?.focus({ preventScroll: true }));
    }
  };

  // Return from the testimony modal all the way to the prayer detail. The
  // testimony layer sits above `leaf` (the Mark-answered / Edit-testimony
  // handoff is additive — the leaf stays mounted), so requestClose('leaf') pops
  // both in one tick. Defensive fallback if the leaf is somehow absent.
  const exitTestimonyToDetail = () => {
    if (nav.has('leaf')) nav.requestClose('leaf');
    else nav.requestClose('testimony');
  };

  // An explicit action committed: capture-mode "Save note and mark answered"
  // and "Mark answered without a note" both write the testimony and Answered
  // status in one transaction (a testimony intent, never status-only). The
  // celebration is armed only here, on a success that closes the dialog.
  const handleTestimonyCommitted = () => {
    if (!prayerEditorIsCurrent(detailAuthRef.current)) return;
    if (testimonyMode === 'capture') pendingCelebrationRef.current = true;
    focusLeafAfterTestimonyRef.current = true;
    exitTestimonyToDetail();
  };

  // A safe dismissal finished (the dialog never writes the prayer for one). An
  // in-app dismissal returns to the prayer; Back steps one level, to the leaf.
  const handleTestimonyDismissed = (via: 'in-app' | 'back') => {
    if (via === 'back') {
      if (nav.has('testimony')) nav.requestClose('testimony');
      return;
    }
    focusLeafAfterTestimonyRef.current = true;
    exitTestimonyToDetail();
  };

  // A2: PrayerDetail child layers above `detail`. Registered here (PrayerDetail
  // mounts with all of them CLOSED -> StrictMode-safe, README §3.2a). leaf +
  // testimony take coordinator z (threaded to their roots); edit-raw / edit-who
  // are inline edit modes (no stacking root) so their returned z is ignored —
  // they are modeled only so Back COMMITS the draft and exits (decision 7).

  // Closing Add update retains its draft. The registered target's existing
  // cleanup stops/stores its capture, including a stop already underway.
  const onLeafClose = () => { setLeafSheetOpen(false); };
  // edit-raw / edit-who close: Back COMMITS the draft (decision 7). The buttons
  // (Save/Cancel/Enter/Escape) handle their own outcome and flip the ref off so
  // this close() does not double-commit; the Back gesture leaves the ref at its
  // default `true`, so it commits the current draft. handleEditWho guards
  // same-value internally; handleEditRawText is guarded here against an empty /
  // unchanged draft (covers the recovery-onTypeMode empty-draft case).
  const onEditRawClose = () => {
    if (rawCommitsOnCloseRef.current) {
      const trimmed = rawDraft.trim();
      if (trimmed && trimmed !== prayer.rawText) {
        rawCommitsOnCloseRef.current = false;
        setRawSavePending(true);
        void handleEditRawText(trimmed)
          .then(() => {
            if (!prayerEditorIsCurrent(rawSessionRef.current.auth)) return;
            setRawSaveError(false);
            setRawSavePending(false);
            rawCommitsOnCloseRef.current = true;
            setEditingRaw(false);
          })
          .catch(() => {
            if (!prayerEditorIsCurrent(rawSessionRef.current.auth)) return;
            setRawSaveError(true);
            setRawSavePending(false);
            rawCommitsOnCloseRef.current = true;
            // requestClose already removed the inline layer. Re-register it so
            // a failed Back-commit leaves the draft visible and Back remains
            // scoped to this editor instead of closing the prayer detail.
            if (!nav.has('edit-raw')) nav.push('edit-raw', onEditRawClose);
          });
        return;
      }
    }
    rawCommitsOnCloseRef.current = true;
    setEditingRaw(false);
  };
  const onEditWhoClose = () => {
    if (whoCommitsOnCloseRef.current) {
      const trimmed = whoDraft.trim();
      if (trimmed) {
        whoCommitsOnCloseRef.current = false;
        setWhoSavePending(true);
        void handleEditWho(trimmed)
          .then(() => {
            if (!prayerEditorIsCurrent(whoSessionRef.current.auth)) return;
            setWhoSaveError(false);
            setWhoSavePending(false);
            whoCommitsOnCloseRef.current = true;
            setEditingWho(false);
          })
          .catch(() => {
            if (!prayerEditorIsCurrent(whoSessionRef.current.auth)) return;
            setWhoSaveError(true);
            setWhoSavePending(false);
            whoCommitsOnCloseRef.current = true;
            if (!nav.has('edit-who')) nav.push('edit-who', onEditWhoClose);
          });
        return;
      }
    }
    whoCommitsOnCloseRef.current = true;
    setEditingWho(false);
  };

  const zLeaf = useNavLayer('leaf', leafSheetOpen, onLeafClose);
  const zTestimony = useNavLayer('testimony', testimonyMode !== 'none', closeTestimony);
  // Tap-to-match overlay: registered closed at mount (StrictMode-safe);
  // sits above `leaf` when opened from the leaf card. No voice to cancel —
  // the sheet's own unmount aborts its in-flight fetch.
  // Read-only saved-verse sheet (rule #80). Opened directly from the
  // detail body's scripture line, so it sits above `detail` (not above
  // the leaf). Registered closed at mount -> StrictMode-safe.
  const zScriptureVerses = useNavLayer('scripture-verses', scriptureVersesOpen, () =>
    setScriptureVersesOpen(false),
  );
  useNavLayer('edit-raw', editingRaw, onEditRawClose);
  useNavLayer('edit-who', editingWho, onEditWhoClose);

  // Settings Open (Step 3; rule #78 A2: the testimony layer always sits above
  // `leaf`, so one step of Back from the dialog is the ordinary `one-step`
  // close). Both opens are closed-to-open edges after mount, never initial
  // state, and each waits until the history entry below it has landed, so no
  // two pushes share a tick. Opening only: it never toggles the
  // prayer's status and writes nothing. A request is used once (the ref keeps
  // StrictMode's re-run from repeating it) and is set aside when anything else
  // is already open or the editor's session changed.
  const initialTestimonyUsedRef = useRef<string | null>(null);
  useEffect(() => {
    const id = initialTestimony;
    if (!id || initialTestimonyUsedRef.current === id) return;
    const setAside = () => {
      initialTestimonyUsedRef.current = id;
      onInitialTestimonyDone?.(id);
    };
    if (isCoreReadOnly() || !prayerEditorIsCurrent(detailAuthRef.current)) {
      setAside();
      return;
    }
    if (testimonyMode !== 'none' || editingRaw || editingWho || scriptureVersesOpen) {
      setAside();
      return;
    }
    // Each push waits for the previous one's history entry (the location
    // echo) before the next edge, as a person's two taps would.
    const depth = (location.state as { navDepth?: number } | null)?.navDepth ?? 0;
    if (depth !== nav.length()) return;
    if (!leafSheetOpen) {
      if (nav.length() !== 1 || !nav.isTopLayer('detail')) {
        setAside();
        return;
      }
      setLeafSheetOpen(true);
      return;
    }
    if (!nav.isTopLayer('leaf')) return;
    initialTestimonyUsedRef.current = id;
    setTestimonyMode(detailStatus === 'Answered' ? 'edit' : 'capture');
    onInitialTestimonyDone?.(id);
  }, [
    initialTestimony, leafSheetOpen, location, testimonyMode, editingRaw, editingWho,
    scriptureVersesOpen, nav, detailStatus, onInitialTestimonyDone,
  ]);

  // Stepped detail-share layers (A1, README §8). Registered here; PrayerDetail
  // mounts with every share boolean false -> StrictMode-safe (§3.2a). Each
  // returns the computed z for the corresponding share sheet root. In A2 the
  // detail-share chain opens from the leaf additively, so it sits above the
  // `leaf` layer (which sits above `detail`).
  const zDetailShareChoice = useNavLayer('detail-share-choice', sendChoiceOpen, () =>
    setSendChoiceOpen(false),
  );
  const zDetailShareText = useNavLayer('detail-share-text', shareOpen, () =>
    setShareOpen(false),
  );
  const zDetailShareMail = useNavLayer('detail-share-mail', mailCardOpen, () =>
    setMailCardOpen(false),
  );

  // Whole-flow close for the detail-share chain (X / Cancel / backdrop):
  // requestClose the shallowest open detail-share layer in one tick. Back
  // steps one screen via the reconciler. Never clear slots directly here.
  const closeShareFlow = () => {
    // A2: the detail-share chain always opens from the leaf (additive), so the
    // X / Cancel / backdrop exits all the way to the prayer by popping the leaf
    // and everything above it (the share chain) in one tick. The shallowest open
    // share key is the A1 fallback if the leaf is somehow unmodeled.
    if (nav.has('leaf')) {
      nav.requestClose('leaf');
      return;
    }
    const key = sendChoiceOpen
      ? 'detail-share-choice'
      : shareOpen
        ? 'detail-share-text'
        : mailCardOpen
          ? 'detail-share-mail'
          : null;
    if (key) nav.requestClose(key);
  };

  // Final-action recheck for the Text and Mail sheets: the prayer must still
  // exist in the live store immediately before render/copy/download/send.
  const authorizeTextOutbound = useCallback(() => {
    return shareOpen && isLiveSharePrayer(prayer.id);
  }, [prayer.id, shareOpen]);

  const authorizeMailOutbound = useCallback(() => {
    return mailCardOpen && isLiveSharePrayer(prayer.id);
  }, [mailCardOpen, prayer.id]);

  return (
    <div
      className={styles.detail}
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label={t('detail.detailLabel')}
    >
      <button
        type="button"
        className={styles.detailBack}
        onClick={handleBack}
        aria-label={t('nav.back')}
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="14 6 8 12 14 18" />
        </svg>
      </button>

      <button
        type="button"
        className={styles.newBtn}
        onClick={onNew}
        aria-label={t('nav.newAria')}
      >
        {t('nav.new')}
      </button>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />

      <div className={styles.detailContent}>
        <header className={styles.detailHeader}>
          <div className={styles.detailMeta}>
            {(() => {
              const visual = STATUS_VISUAL[detailStatus];
              return (
                <>
                  <span className={`${styles.statusDot} ${visual.dot}`} aria-hidden="true" />
                  <span className={`${styles.detailStatusLabel} ${visual.label}`}>{detailStatus}</span>
                </>
              );
            })()}
            {/* E-cleanup: single-prayer PDF download removed in Chunk E.3.
                Surfaces still on disk pending a future cleanup chunk:
                  - .exportInline CSS in PrayerDetail.module.css
                  - i18n key `detail.exportLabel` (en/es/pt)
                  - sharePrayerExport in src/lib/export-prayer.tsx
                  - formatPrayerExport in src/lib/export-prayer.tsx
                  - PrayerDocument component in src/components/PrayerDocument.tsx
                The multi-prayer keepsake PDF (PersonPrayerBookDocument /
                sharePersonBookExport) is unrelated and stays live. */}
          </div>
          {editingWho ? (
            <div className={styles.whoEditBlock}>
              <div className={styles.whoEditRow}>
                <input
                  className={styles.whoEditInput}
                  value={whoDraft}
                  onChange={(e) => { setWhoSaveError(false); setWhoDraft(e.target.value); }}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      whoCommitsOnCloseRef.current = false;
                      const trimmed = whoDraft.trim();
                      setWhoSavePending(true);
                      try {
                        if (trimmed) await handleEditWho(trimmed);
                        if (!prayerEditorIsCurrent(whoSessionRef.current.auth)) return;
                        setWhoSaveError(false);
                        setEditingWho(false);
                      } catch {
                        if (!prayerEditorIsCurrent(whoSessionRef.current.auth)) return;
                        whoCommitsOnCloseRef.current = true;
                        setWhoSaveError(true);
                      } finally {
                        setWhoSavePending(false);
                      }
                    }
                    if (e.key === 'Escape') {
                      whoCommitsOnCloseRef.current = false;
                      setWhoSaveError(false);
                      setWhoDraft(whoOf(prayer));
                      setEditingWho(false);
                    }
                  }}
                  placeholder={t('detail.whoPlaceholder')}
                  aria-label={t('detail.whoPlaceholder')}
                  disabled={whoSavePending}
                  autoFocus
                />
                <button
                  type="button"
                  className={styles.whoEditSave}
                  disabled={whoSavePending}
                  onClick={async () => {
                    whoCommitsOnCloseRef.current = false;
                    const trimmed = whoDraft.trim();
                    setWhoSavePending(true);
                    try {
                      if (trimmed) await handleEditWho(trimmed);
                        if (!prayerEditorIsCurrent(whoSessionRef.current.auth)) return;
                      setWhoSaveError(false);
                      setEditingWho(false);
                    } catch {
                      if (!prayerEditorIsCurrent(whoSessionRef.current.auth)) return;
                      whoCommitsOnCloseRef.current = true;
                      setWhoSaveError(true);
                    } finally {
                      setWhoSavePending(false);
                    }
                  }}
                >
                  {t('detail.save')}
                </button>
                <button
                  type="button"
                  className={styles.whoEditCancel}
                  disabled={whoSavePending}
                  onClick={() => { whoCommitsOnCloseRef.current = false; setWhoSaveError(false); setWhoDraft(whoOf(prayer)); setEditingWho(false); }}
                >
                  {t('detail.cancel')}
                </button>
              </div>
              {whoSaveError && (
                <p className={styles.inlineError} role="alert">{t('detail.saveError')}</p>
              )}
            </div>
          ) : (() => {
            const rawWho = whoOf(prayer);
            const displayName = isPraise(prayer) ? t('entry.praiseLabel') : isSelf(rawWho) ? t('people.you') : rawWho;
            // Nesting cap (rule #78 A3 / README §3.4 round-11 B2): the who-name
            // person-tap is interactive ONLY from a TOP-LEVEL detail's BASE view
            // (no leaf / testimony / edit child open) — the `person-detail` mount
            // passes no onPersonTap, so it is already non-interactive there. This
            // base-view gate is largely defensive (the leaf/testimony backdrops
            // already cover the header; editingWho replaces this branch) but makes
            // the cap explicit so the who-name is never the trigger while a child
            // layer is the top of the stack.
            const canOpenPerson =
              Boolean(onPersonTap) && rawWho !== 'General' &&
              !leafSheetOpen && testimonyMode === 'none' && !editingRaw;
            return (
              <div className={styles.whoRow}>
                {canOpenPerson ? (
                  <button
                    type="button"
                    className={`${styles.detailWho} ${styles.detailWhoBtn}`}
                    onClick={() => onPersonTap!(rawWho)}
                    aria-label={t('people.prayersFor', { name: displayName })}
                  >
                    {displayName}
                  </button>
                ) : (
                  <h1 className={styles.detailWho}>{displayName}</h1>
                )}
                {/* Cutover: renaming `who` is a prayer write. */}
                {!isCoreReadOnly() && (
                <button
                  type="button"
                  className={styles.whoEditLink}
                  onClick={() => { whoCommitsOnCloseRef.current = true; setWhoSaveError(false); setWhoDraft(rawWho); whoBaseRef.current = prayer; whoSessionRef.current = new PrayerAuthoringSession(prayerEditorAuth()); setEditingWho(true); }}
                >
                  {t('detail.edit')}
                </button>
                )}
              </div>
            );
          })()}

          <p className={styles.detailDateLine}>{formatFullDateTime(prayer.createdAt)}</p>

          {/* Inline NameHint — appears only when parsed.who === 'General'
              AND rawText shows unnamed-third-party markers AND the user
              hasn't already manually set `who` (see shouldShowNameHint).
              Renders null otherwise; layout is byte-identical to today
              for prayers that don't trigger. The `key` reset forces the
              component to remount with mode='hint' on every pencil
              cycle so a previous 'expanded' state never carries over. */}
          {!isCoreReadOnly() && (
            <NameHintInline
              key={editingWho ? 'pencil' : 'idle'}
              triggerActive={showNameHint}
              isPencilEditing={editingWho}
              onKeepGeneral={handleKeepGeneral}
              onRequestRename={() => {
                whoCommitsOnCloseRef.current = true;
                setWhoSaveError(false);
                setWhoDraft(whoOf(prayer));
                whoBaseRef.current = prayer; whoSessionRef.current = new PrayerAuthoringSession(prayerEditorAuth()); setEditingWho(true);
              }}
            />
          )}

          {showMovingSuggestion && !isCoreReadOnly() && (
            <div className={styles.movingSuggestion}>
              <span className={styles.movingDot} aria-hidden="true" />
              <span className={styles.movingText}>{t('detail.movingHint')}</span>
              <button
                type="button"
                className={styles.movingAction}
                onClick={handleMarkMoving}
              >
                {t('detail.markMoving')}
              </button>
            </div>
          )}
        </header>

        {prayer.gardenSummary && (
          <p className={styles.storySoFar}>{prayer.gardenSummary}</p>
        )}

        {/* V.B.2: recovery banner when the prayer's audio terminally
            failed transcription. Hosts Type / Retry / Discard. The
            "Type" button flips editingRaw + sets the draft to empty
            so the user can start fresh. Banner auto-unmounts when
            transcriptionFailed clears (via the parent re-render).

            Core-content cutover: hidden while read-only, matching the
            edit pencil and the leaf sheet below. All three of its
            actions resolve to writes this release rejects, and two of
            them mislead rather than just failing: Type opens the raw
            editor (which the pencil gate otherwise keeps shut) and
            invites the user to re-type prayer text that the guard then
            drops, and Discard reported a deletion that never happened.
            The audio and the prayer both stay exactly where they are,
            which is the outcome the cutover wants. */}
        {recoveryBannerShown && (
          <TranscriptionRecoveryBanner
            prayer={prayer}
            parked={!enhancedVoiceAllowed() || !prayer.transcriptionFailed}
            attention={pendingAttention}
            onTypeMode={() => {
              rawCommitsOnCloseRef.current = true;
              setRawDraft('');
              rawBaseRef.current = prayer; rawSessionRef.current = new PrayerAuthoringSession(prayerEditorAuth()); setEditingRaw(true);
            }}
            onDeleted={onClose}
          />
        )}

        {/* D4 (2026-10-18): a bound audio row of this prayer whose save the
            server has not confirmed (a restored device, receipt_missing). The
            notice is read-only (the words plus Copy), renders in read-only
            mode too, and never retries, rebinds, merges or removes. */}
        <UnconfirmedSaveNotice prayer={prayer} />

        {/* Read-only scripture context for a Pray Mode prayer: the passage
            the user prayed on (intro + reference + BSB verse text). Above the
            raw prayer; no edit affordance in v1. Spacing comes from
            .detailContent's gap. Renders only from scriptureAnchor data. */}
        {prayer.scriptureAnchor ? (
          <div className={styles.scriptureBlock}>
            <p className={styles.scriptureIntro}>{t('prayScripture.intro')}</p>
            <p className={styles.scriptureRef}>{prayer.scriptureAnchor.reference}</p>
            <div className={styles.scriptureText}>{prayer.scriptureAnchor.text}</div>
          </div>
        ) : null}

        {parseNeedsAttention && (
          <div className={styles.parsePending} role="status">
            <span>{t('detail.parsePending')}</span>
            <button
              type="button"
              className={styles.inlineRetry}
              onClick={handleRetryParse}
              disabled={retryingParse}
            >
              {t('detail.retryParse')}
            </button>
          </div>
        )}

        <div className={styles.detailRawWrap}>
          {editingRaw ? (
            <>
              <textarea
                className={styles.detailRawEdit}
                value={rawDraft}
                onChange={(e) => { setRawSaveError(false); setRawDraft(e.target.value); }}
                aria-label={t('detail.editPrayerInputAria')}
                rows={4}
                disabled={rawSavePending}
              />
              {rawSaveError && (
                <p className={styles.inlineError} role="alert">{t('detail.saveError')}</p>
              )}
              <div className={styles.detailRawActions}>
                <button
                  type="button"
                  className={styles.historyEditSave}
                  disabled={rawSavePending}
                  onClick={async () => {
                    rawCommitsOnCloseRef.current = false;
                    const trimmed = rawDraft.trim();
                    setRawSavePending(true);
                    try {
                      if (trimmed) await handleEditRawText(trimmed);
                      if (!prayerEditorIsCurrent(rawSessionRef.current.auth)) return;
                      setRawSaveError(false);
                      setEditingRaw(false);
                    } catch {
                      if (!prayerEditorIsCurrent(rawSessionRef.current.auth)) return;
                      rawCommitsOnCloseRef.current = true;
                      setRawSaveError(true);
                    } finally {
                      setRawSavePending(false);
                    }
                  }}
                >
                  {t('detail.save')}
                </button>
                <button
                  type="button"
                  className={styles.historyEditCancel}
                  disabled={rawSavePending}
                  onClick={() => { rawCommitsOnCloseRef.current = false; setRawSaveError(false); setRawDraft(prayer.rawText); setEditingRaw(false); }}
                >
                  {t('detail.cancel')}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* V.B.2: empty rawText + transcriptionFailed renders an
                  italic placeholder so the empty blockquote reads as
                  intentional rather than missing data. The Copy/Edit
                  buttons stay hidden when there's nothing to copy. */}
              {(prayer.transcriptionFailed ||
                (prayer.pendingTranscriptionId && !enhancedVoiceAllowed()) ||
                pendingAttention) &&
              !prayer.rawText.trim() ? (
                <blockquote className={`${styles.detailRaw} ${styles.detailRawPlaceholder}`}>
                  {t(
                    pendingAttention === 'mismatch' || pendingAttention === 'missing'
                      ? 'voice.mismatchRawPlaceholder'
                      : pendingAttention === 'unreadable'
                        ? 'voice.unreadableRawPlaceholder'
                        : 'voice.recoveryRawPlaceholder',
                  )}
                </blockquote>
              ) : (
                <>
                  <blockquote className={styles.detailRaw}>{prayer.rawText}</blockquote>
                  <div className={styles.detailRawBtns}>
                    <CopyButton text={prayer.rawText} label={t('detail.copyPrayerAria')} />
                    {/* Cutover: editing the raw prayer is a write. Copy stays. */}
                    {!isCoreReadOnly() && (
                    <button
                      type="button"
                      className={styles.historyEditBtn}
                      onClick={() => { rawCommitsOnCloseRef.current = true; setRawSaveError(false); setRawDraft(prayer.rawText); rawBaseRef.current = prayer; rawSessionRef.current = new PrayerAuthoringSession(prayerEditorAuth()); setEditingRaw(true); }}
                      aria-label={t('detail.editPrayerAria')}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Read-only "Scripture for this prayer" line (rule #80). Shows
            only when a SHARE already saved verses onto this prayer and
            they resolve from the local bundle. Tappable readLink register
            (bordered gold pill + chevron). Hidden while the raw text is
            being edited. Opens the read-only ScriptureVersesSheet — never
            a search. */}
        {showScriptureLine && !editingRaw && (
          <button
            type="button"
            className={styles.scriptureLine}
            onClick={() => setScriptureVersesOpen(true)}
          >
            <span>{t('scripture.detailLine')}</span>
            <svg
              className={styles.scriptureLineChevron}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
        )}

        {/* Timeline of progress entries. Conditional on history existing
            so an empty prayer doesn't show a stranded section header.
            The Sacred Leaf below is the entry point for adding NEW
            updates (via the leaf sheet's UpdateComposer). */}
        {detailHistory.length > 0 && (
          <div className={styles.detailSection}>
            <p className={styles.sectionLabel}>{t('detail.progress')}</p>
            <ul className={styles.historyList}>
              {detailHistory.map((entry) => (
                <EditableHistoryEntry
                  key={entry.date}
                  date={entry.date}
                  note={entry.note}
                  onSave={(newNote) => handleEditHistoryEntry(entry, newNote)}
                  onRemove={() => handleRemoveHistoryEntry(entry)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Sacred Leaf: floating action button at the bottom of the
          viewport. Tapping opens the action sheet with all the
          prayer-affordances in one place. The button plays a
          persistent breathing halo via its own CSS — no state from
          here. */}
      {/* Core-content cutover: FOUR of the leaf sheet's five cards are
          prayer writes (Add update, Mark answered / Edit testimony, Hold
          this one close, Delete), and the sheet is a shared component
          this chunk does not own, so the whole leaf entry is hidden while
          read-only rather than rendering four dead rows. The fifth card,
          Send a card, is a read and stays reachable from the Garden vine
          card's share glyph. */}
      {!isCoreReadOnly() && (
      <LeafButton
        ref={leafButtonRef}
        onClick={() => setLeafSheetOpen(true)}
        ariaLabel={t('leaf.openAria')}
      />
      )}
      {leafSheetOpen && !isCoreReadOnly() && (
        <LeafActionSheet
          zIndex={zLeaf}
          prayer={prayer}
          onClose={() => setLeafSheetOpen(false)}
          onSendCard={handleSendCardFromLeaf}
          onAnswerAction={handleToggleAnswered}
          onEditTestimony={openEditTestimony}
          onDelete={handleDelete}
          onReopen={handleToggleAnswered}
          deleteState={leafDeleteState}
        />
      )}

      {scriptureVersesOpen && (
        <ScriptureVersesSheet
          zIndex={zScriptureVerses}
          verses={scriptureVerses}
          onClose={() => nav.requestClose('scripture-verses')}
        />
      )}

      {testimonyMode !== 'none' && !isCoreReadOnly() && (
        <TestimonyModal
          zIndex={zTestimony}
          prayer={prayer}
          mode={testimonyMode}
          keyboardInset={testimonyKeyboardInset}
          controlRef={testimonyControlRef}
          onCommitted={handleTestimonyCommitted}
          onDismissed={handleTestimonyDismissed}
        />
      )}

      {shareOpen && (
        <LetThemKnowSheet
          zIndex={zDetailShareText}
          prayer={prayer}
          onClose={closeShareFlow}
          authorizeOutbound={authorizeTextOutbound}
        />
      )}

      {sendChoiceOpen && PICKER_ENABLED && (
        <ChoiceSheet
          zIndex={zDetailShareChoice}
          onClose={closeShareFlow}
          onSendAsText={() => {
            // Additive: keep sendChoiceOpen so detail-share-choice stays
            // mounted under detail-share-text and back steps one screen.
            if (isLiveSharePrayer(prayer.id)) setShareOpen(true);
          }}
          onMailACard={() => {
            if (isLiveSharePrayer(prayer.id)) setMailCardOpen(true);
          }}
        />
      )}

      {mailCardOpen && PICKER_ENABLED && (
        <MailCardSheet
          zIndex={zDetailShareMail}
          prayer={prayer}
          onClose={closeShareFlow}
          authorizeOutbound={authorizeMailOutbound}
          onContinue={(payload) => {
            console.info('[mailcard] redacted payload:', payload);
          }}
        />
      )}

      {celebrating && (
        <div
          className={styles.celebration}
          role="status"
          aria-live="polite"
          aria-label={t('pray.answeredCelebration')}
        >
          <div className={styles.celebrationHalo} aria-hidden="true" />
          <svg
            className={styles.celebrationBloom}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19.8 2c1 3 .5 4.5-1.5 10-.5 1.5-1.5 3.5-2.5 5" />
            <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
          </svg>
          <span className={styles.celebrationText}>
            {t('pray.answeredCelebration')}
          </span>
        </div>
      )}
    </div>
  );
}

// Same focusable list as useDialogFocus (shared, kept unchanged); used only
// for the sheet's own Tab and Shift+Tab.
const SHEET_FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function sheetFocusables(sheet: HTMLElement): HTMLElement[] {
  return Array.from(sheet.querySelectorAll<HTMLElement>(SHEET_FOCUSABLE)).filter(
    (element) => element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0,
  );
}

function owningDialog(element: HTMLElement): HTMLElement | null {
  return element.closest<HTMLElement>('[role="dialog"][aria-modal="true"]');
}

/**
 * Testimony capture / edit modal. Mounted only when testimonyMode !== 'none'
 * so each open starts from the stored draft and attempt state. Safe dismissal
 * (docs/plans/testimony-safe-dismissal-2026-10-15-rev4.md): an outside tap,
 * Escape, "Not now" / "Close" and one step of Back keep the typed note as a
 * device-local draft and never write the prayer; only "Save" and "Mark
 * answered without a note" do. The parent owns the celebration and the layer;
 * this modal owns the text, the draft and the recording holds.
 */
function TestimonyModal({
  prayer,
  mode,
  keyboardInset,
  controlRef,
  onCommitted,
  onDismissed,
  zIndex,
}: {
  prayer: Prayer;
  mode: 'capture' | 'edit';
  keyboardInset: number;
  controlRef: RefObject<TestimonyLayerControl | null>;
  onCommitted: (purpose: TestimonyDraftPurpose) => void;
  onDismissed: (via: 'in-app' | 'back') => void;
  /** Coordinator-computed z for the `testimony` layer (A2). */
  zIndex?: number;
}) {
  const { t } = useTranslation();
  const editor = useSafeTestimonyEdit({
    prayer,
    mode,
    voiceTarget: `testimony-${prayer.id}`,
    onCommitted,
    onDismissed,
  });

  // The layer handle exists only while this dialog is mounted, so a late
  // layer close after unmount can never cancel or hold anything.
  const layerControl = editor.layerControl;
  useEffect(() => {
    controlRef.current = layerControl;
    return () => {
      if (controlRef.current === layerControl) controlRef.current = null;
    };
  }, [controlRef, layerControl]);

  const noticeClearance = useNoticeStackClearance();
  // Keyboard room (v0.2.0 follow-through): while the keyboard is open (the
  // rule #11 100 px threshold) AND a notice shows, the overlay ends at the
  // keyboard's top edge, asks the notice stack for the room the text box and
  // the Save / Not now row need, keeps the box in view and marks itself
  // `data-keyboard-room`, so that row sticks above the keyboard. Nothing
  // changes otherwise.
  const keyboardRoom = keyboardInset > 100 && noticeClearance > 0;
  const overlayRef = useRef<HTMLDivElement>(null);
  useNoticeStackKeyboardRoom(keyboardRoom, overlayRef);
  const sheetRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const confirmSafeRef = useRef<HTMLButtonElement>(null);
  const requestDismiss = editor.requestDismiss;
  const onEscape = useCallback(() => {
    void requestDismiss('escape');
  }, [requestDismiss]);
  // Focus opens on the sheet, never on the box: the box is read-only until the
  // stored draft is read, and iPhone Safari left a box focused while read-only
  // (or by script) with a caret and no keyboard. The person's own tap is the
  // box's first focus.
  useDialogFocus({ open: true, containerRef: sheetRef, initialFocusRef: sheetRef, onEscape });

  // The shared helper leaves Tab and Shift+Tab to the browser while the sheet
  // itself has focus, and Shift+Tab would leave the dialog. Keep both inside.
  const onSheetKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    const sheet = sheetRef.current;
    if (event.key !== 'Tab' || !sheet || event.target !== sheet) return;
    if (topmostModal() !== owningDialog(sheet)) return;
    event.preventDefault();
    const focusable = sheetFocusables(sheet);
    const next = event.shiftKey ? focusable[focusable.length - 1] : focusable[0];
    (next ?? sheet).focus({ preventScroll: true });
  }, []);

  // Focus follows a new failure, and a confirmation opens on its safe choice.
  const failureKey = editor.error ?? (editor.phase === 'unavailable' ? 'lookup' : null);
  useEffect(() => {
    if (failureKey) alertRef.current?.focus({ preventScroll: true });
  }, [failureKey]);
  useEffect(() => {
    if (editor.confirm) confirmSafeRef.current?.focus({ preventScroll: true });
  }, [editor.confirm]);

  // A box that gained focus while read-only (a tap during the lookup or under
  // a set-aside note) and still holds it when it turns editable: move focus to
  // the sheet so the next tap on the box is a new focus. Only on that
  // transition, only from the box, only while this dialog is topmost; declared
  // after the effects above so a failure or a confirmation keeps its focus.
  const wasEditableRef = useRef(editor.editable);
  useEffect(() => {
    const wasEditable = wasEditableRef.current;
    wasEditableRef.current = editor.editable;
    if (wasEditable || !editor.editable) return;
    const sheet = sheetRef.current;
    if (!sheet || document.activeElement !== textareaRef.current) return;
    if (topmostModal() !== owningDialog(sheet)) return;
    sheet.focus({ preventScroll: true });
  }, [editor.editable]);

  const showTestimonyCue = useSaveCue(
    editor.isListening || editor.isTranscribing,
    editor.displayValue,
  );
  const capturing = editor.isListening || editor.isTranscribing;
  const action = editor.unfinishedAction;
  const conflict = editor.conflict;
  const quoted = (text: string | null) => (text && text.trim() ? text : t('testimonyDraft.noNote'));

  const confirmPanel = (() => {
    switch (editor.confirm) {
      case 'without-note':
        return {
          message: t('testimonyDraft.withoutNoteConfirm'),
          safe: t('testimonyDraft.keepWriting'),
          onSafe: editor.cancelConfirm,
          act: t('testimonyDraft.discardAndMark'),
          onAct: () => { void editor.confirmWithoutNote(); },
        };
      case 'discard':
        return {
          message: t('testimonyDraft.discardConfirm'),
          safe: t('testimonyDraft.keep'),
          onSafe: editor.cancelConfirm,
          act: t('testimonyDraft.discardDraft'),
          onAct: () => { void editor.discardDraft(); },
        };
      case 'leave':
        return {
          message: t('testimonyDraft.leaveConfirm'),
          safe: t('testimonyDraft.stay'),
          onSafe: editor.cancelConfirm,
          act: t('testimonyDraft.leaveWithoutKeeping'),
          onAct: editor.leaveWithoutKeeping,
        };
      case 'remove-note':
        return {
          message: t('testimonyDraft.removeNoteConfirm', {
            note: conflict?.kind === 'prayer' ? quoted(conflict.savedNote) : '',
          }),
          safe: t('testimonyDraft.keepMyNote'),
          onSafe: editor.cancelConfirm,
          act: t('testimonyDraft.removeNoteAndMark'),
          onAct: () => editor.rearmAgainstCurrent('without-note'),
        };
      default:
        return null;
    }
  })();

  const backdropStyle: CSSProperties = { zIndex };
  if (keyboardInset) {
    // Lift above the keyboard AND clamp the top so the flex-centered
    // testimony sheet can't climb into the Dynamic Island / notch when
    // vertical space is compressed by the keyboard (62px island on
    // iPhone 16/17 Pro, 47-59px elsewhere).
    backdropStyle.paddingTop = 'max(env(safe-area-inset-top, 0px), 24px)';
    backdropStyle.paddingBottom = `${keyboardInset + 24}px`;
  }
  const keyboardScroll = keyboardInset > 100 && noticeClearance === 0;
  if (keyboardScroll) {
    // Keep the complete recording controls reachable above the keyboard even
    // without a notice. The existing notice-driven action layout stays separate.
    backdropStyle.bottom = `${keyboardInset}px`;
    backdropStyle.paddingBottom = '24px';
    backdropStyle.alignItems = 'flex-start';
    backdropStyle.overflowY = 'auto';
  }
  if (noticeClearance > 0) {
    // App notice stack (v0.2.0 finishing): the stack is fixed above this
    // dialog, so while a notice shows the card starts at or below the stack's
    // clearance (always below the safe area). The card stays centred in the
    // room below through its auto block margins while it fits, starts at the
    // clearance when it does not, and the overlay then scrolls, so the text
    // box and every control stay reachable.
    backdropStyle.paddingTop = `${noticeClearance}px`;
    backdropStyle.alignItems = 'flex-start';
    backdropStyle.overflowY = 'auto';
  }
  if (keyboardRoom) {
    // The scroll area ends at the keyboard's top edge (the dimmed area behind
    // the keyboard is not visible), so the sticky action row sits directly
    // above the keyboard; the card's own padding ends the scroll.
    backdropStyle.bottom = `${keyboardInset}px`;
    backdropStyle.paddingBottom = '0px';
  }

  // In keyboard room the sticky row holds the cancel control and the primary
  // action only; "Mark answered without a note" moves just below it in the
  // same order (reached by scrolling), so the two controls a person needs
  // while typing fit above the keyboard on a small phone.
  const withoutNoteButton = editor.showWithoutNote && !action ? (
    <button
      type="button"
      className={styles.testimonySecondary}
      aria-disabled={!editor.editable || undefined}
      onClick={() => { if (editor.editable) editor.markWithoutNote(); }}
    >
      {t('testimonyDraft.markWithoutNote')}
    </button>
  ) : null;

  return (
    <div
      ref={overlayRef}
      className={styles.testimonyBackdrop}
      onClick={() => { void editor.requestDismiss('outside'); }}
      role="dialog"
      aria-modal="true"
      aria-label={mode === 'capture' ? t('evidence.addTestimonyTitle') : t('evidence.editTestimony')}
      style={backdropStyle}
      data-keyboard-room={keyboardRoom ? '' : undefined}
    >
      <div
        ref={sheetRef}
        className={styles.testimonySheet}
        style={noticeClearance > 0 || keyboardScroll ? { marginBlock: 'auto' } : undefined}
        tabIndex={-1}
        onKeyDown={onSheetKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.testimonyTitle}>
          {mode === 'capture'
            ? t('evidence.addTestimonyTitle')
            : t('evidence.editTestimony')}
        </h2>
        {mode === 'capture' && (
          <p className={styles.testimonySub}>{t('evidence.addTestimonySub')}</p>
        )}

        <div className={styles.testimonyLines} role="status" aria-live="polite">
          {editor.restored && <p className={styles.testimonyLine}>{t('testimonyDraft.restored')}</p>}
          {editor.changedSince && <p className={styles.testimonyLine}>{t('testimonyDraft.changedSince')}</p>}
          {editor.undeterminedMatchesSaved && (
            <p className={styles.testimonyLine}>{t('testimonyDraft.matchesSaved')}</p>
          )}
          {action && !conflict && (
            <p className={styles.testimonyLine}>
              {action.purpose === 'save'
                ? t('testimonyDraft.saveDidNotFinish')
                : t('testimonyDraft.answeredDidNotFinish')}
            </p>
          )}
          {editor.unfinished?.kind === 'independent' && (
            <p className={styles.testimonyLine}>{t('testimonyDraft.independentAttempt')}</p>
          )}
          {editor.unfinished?.kind === 'settings' && (
            <p className={styles.testimonyLine}>{t('testimonyDraft.settingsAttempt')}</p>
          )}
          {editor.stillSaving && <p className={styles.testimonyLine}>{t('testimonyDraft.stillSaving')}</p>}
        </div>

        {editor.setAside !== null && (
          <div className={styles.testimonyAside}>
            <p className={styles.testimonyLine}>{t('testimonyDraft.setAside')}</p>
            <p className={styles.testimonyQuote}>{editor.setAside}</p>
            <p className={styles.testimonyLine}>{t('testimonyDraft.setAsideFirst')}</p>
            <div className={styles.testimonyRow}>
              <button type="button" className={styles.inlineRetry} onClick={editor.adoptSetAside}>
                {t('testimonyDraft.useSetAside')}
              </button>
              <CopyButton text={editor.setAside} label={t('testimonyDraft.copyAria')} />
              <button
                type="button"
                className={styles.testimonyTextButton}
                onClick={() => { void editor.discardSetAside(); }}
              >
                {t('testimonyDraft.discardSetAside')}
              </button>
            </div>
          </div>
        )}

        <div className={styles.testimonyField}>
          <textarea
            ref={textareaRef}
            className={styles.testimonyTextarea}
            value={editor.displayValue}
            onChange={editor.handleChange}
            placeholder={t('evidence.testimonyPlaceholder')}
            aria-label={t('evidence.testimonyInputAria')}
            maxLength={300}
            rows={4}
            readOnly={!editor.editable}
            aria-readonly={!editor.editable || undefined}
          />
          {editor.note.trim() !== '' && (
            <div className={styles.testimonyCopy}>
              <CopyButton text={editor.note} label={t('testimonyDraft.copyAria')} />
            </div>
          )}
        </div>

        {editor.recordingOffers.map((item) => (
          <div key={item.id} className={styles.testimonyOffer} role="status">
            <span>{t('testimonyDraft.recordingOffer')}</span>
            <button
              type="button"
              className={styles.inlineRetry}
              aria-disabled={!editor.editable || undefined}
              onClick={() => editor.addRecordingWords(item.id)}
            >
              {t('testimonyDraft.addItsWords')}
            </button>
          </div>
        ))}

        {conflict?.kind === 'draft' && (
          <div className={styles.inlineError} role="alert">
            <span>{t('testimonyDraft.otherVersionChanged')}</span>
            {conflict.shown && (
              <>
                <span className={styles.testimonyLine}>{t('testimonyDraft.otherVersionLabel')}</span>
                <p className={styles.testimonyQuote}>{quoted(conflict.otherText)}</p>
              </>
            )}
            <div className={styles.testimonyRow}>
              <button type="button" className={styles.inlineRetry} onClick={() => { void editor.keepOnScreen(); }}>
                {t('testimonyDraft.keepOnScreen')}
              </button>
              {!conflict.shown && (
                <button type="button" className={styles.testimonyTextButton} onClick={editor.showOtherVersion}>
                  {t('testimonyDraft.showOther')}
                </button>
              )}
            </div>
          </div>
        )}

        {conflict?.kind === 'prayer' && (
          <div className={styles.inlineError} role="alert">
            <span>
              {conflict.purpose === 'without-note' && conflict.answered
                ? t('testimonyDraft.alreadyAnswered')
                : conflict.savedNote === null
                  ? t('testimonyDraft.changedElsewhereNoNote')
                  : t('testimonyDraft.changedElsewhere')}
            </span>
            {conflict.savedNote !== null && <p className={styles.testimonyQuote}>{conflict.savedNote}</p>}
            <div className={styles.testimonyRow}>
              {action && (
                <button type="button" className={styles.inlineRetry} onClick={() => { void editor.keepMyNote(); }}>
                  {t('testimonyDraft.keepMyNote')}
                </button>
              )}
              {conflict.purpose === 'save' && (
                <button
                  type="button"
                  className={styles.testimonyTextButton}
                  onClick={() => editor.rearmAgainstCurrent('save')}
                >
                  {t('testimonyDraft.saveMineAnyway')}
                </button>
              )}
              {conflict.purpose === 'without-note' && !conflict.answered && (
                <button
                  type="button"
                  className={styles.testimonyTextButton}
                  onClick={conflict.savedNote === null
                    ? () => editor.rearmAgainstCurrent('without-note')
                    : editor.askRemoveNote}
                >
                  {t('testimonyDraft.markWithoutNote')}
                </button>
              )}
            </div>
          </div>
        )}

        {(failureKey || editor.hasFailedRecovery || editor.hasParkedRecovery) && (
          <div ref={alertRef} tabIndex={-1} className={styles.inlineError} role="alert">
            {failureKey === 'lookup' && (
              <>
                <span>{t('testimonyDraft.lookupFailed')}</span>
                <button type="button" className={styles.inlineRetry} onClick={editor.retryLookup}>
                  {t('testimonyDraft.tryAgain')}
                </button>
              </>
            )}
            {failureKey === 'keep' && (
              <>
                <span>{t('testimonyDraft.keepFailed')}</span>
                <div className={styles.testimonyRow}>
                  <button type="button" className={styles.inlineRetry} onClick={editor.retryKeep}>
                    {t('testimonyDraft.tryAgain')}
                  </button>
                  <button
                    type="button"
                    className={styles.testimonyTextButton}
                    onClick={editor.askLeaveWithoutKeeping}
                  >
                    {t('testimonyDraft.leaveWithoutKeeping')}
                  </button>
                </div>
              </>
            )}
            {failureKey === 'save' && <span>{t('detail.saveError')}</span>}
            {failureKey === 'saveReopen' && <span>{t('voice.saveNeedsReopen')}</span>}
            {failureKey === 'identity' && (
              <>
                <span>{t('testimonyDraft.identityChanged')}</span>
                <button
                  type="button"
                  className={styles.testimonyTextButton}
                  onClick={editor.askLeaveWithoutKeeping}
                >
                  {t('testimonyDraft.leaveWithoutKeeping')}
                </button>
              </>
            )}
            {failureKey === 'recording' && <span>{t('testimonyDraft.recordingNotSaved')}</span>}
            {!failureKey && (editor.hasFailedRecovery || editor.hasParkedRecovery) && (
              <>
                <span>
                  {t(editor.hasParkedRecovery ? 'detail.recoveryParked' : 'detail.recoveryFailed')}
                </span>
                {editor.canRetryRecovery && (
                  <button type="button" className={styles.inlineRetry} onClick={editor.retryRecovery}>
                    {t('detail.retryRecovery')}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {editor.isSupported && (
          <div className={styles.testimonyMicArea}>
            <MicButton
              size="lg"
              isListening={editor.isListening}
              isTranscribing={editor.isTranscribing}
              showLabel
              onClick={editor.toggleMic}
              disabled={editor.busy || editor.closing}
              ariaLabel={editor.isListening ? t('detail.stopRecording') : t('detail.voiceInput')}
            />
          </div>
        )}
        {editor.stopFirst && (
          <p className={styles.testimonyStopFirst} role="status">
            {t('testimonyDraft.stopRecordingFirst')}
          </p>
        )}

        {/* Whole actions row hidden during recording/transcribing so the mic
            is the ONLY action visible (rule #22). Exits are held meanwhile:
            the mic is the only stop (A2), and after Stop they wait until the
            capture is stored on this device (C2). */}
        {!capturing && confirmPanel && (
          <div className={styles.testimonyConfirm} role="group" aria-label={confirmPanel.message}>
            <p className={styles.testimonyConfirmText}>{confirmPanel.message}</p>
            <div className={styles.testimonyActions}>
              <button
                ref={confirmSafeRef}
                type="button"
                className={styles.testimonySecondary}
                onClick={confirmPanel.onSafe}
              >
                {confirmPanel.safe}
              </button>
              <button type="button" className={styles.testimonyDanger} onClick={confirmPanel.onAct}>
                {confirmPanel.act}
              </button>
            </div>
          </div>
        )}
        {!capturing && !confirmPanel && (
          <>
            {showTestimonyCue && (
              <div className={styles.testimonyHint} aria-hidden="true">
                {t('evidence.testimonyHint')}
              </div>
            )}
            <div className={styles.testimonyActions} data-keyboard-actions="">
              <button
                type="button"
                className={styles.testimonySecondary}
                aria-disabled={editor.busy || editor.closing || undefined}
                onClick={() => { void editor.requestDismiss('button'); }}
              >
                {mode === 'capture' ? t('testimonyDraft.notNow') : t('testimonyDraft.close')}
              </button>
              {!keyboardRoom && withoutNoteButton}
              {action && !conflict ? (
                <button
                  type="button"
                  className={styles.testimonyPrimary}
                  aria-disabled={!editor.editable || undefined}
                  onClick={() => {
                    if (!editor.editable) return;
                    if (action.purpose === 'save') void editor.save();
                    else void editor.confirmWithoutNote();
                  }}
                >
                  {t('testimonyDraft.tryAgain')}
                </button>
              ) : (
                <button
                  type="button"
                  className={`${styles.testimonyPrimary} ${showTestimonyCue && editor.canSave ? styles.testimonyPrimaryCue : ''}`}
                  aria-disabled={!editor.canSave || undefined}
                  onClick={() => { if (editor.canSave) void editor.save(); }}
                >
                  {mode === 'capture' ? t('testimonyDraft.saveAndMark') : t('testimonyDraft.save')}
                </button>
              )}
            </div>
            {keyboardRoom && withoutNoteButton && (
              <div className={styles.testimonyActionsMore}>{withoutNoteButton}</div>
            )}
            {editor.canDiscardDraft && (
              <button
                type="button"
                className={`${styles.testimonyTextButton} ${styles.testimonyDiscard}`}
                onClick={() => { void editor.openDiscardConfirm(); }}
              >
                {t('testimonyDraft.discardDraft')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
